import React from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, ContactShadows, Environment } from '@react-three/drei';

import ConicalFlask from './lab/shared/ConicalFlask';
import RubberStopper from './lab/shared/RubberStopper';
import DeliveryTube from './lab/shared/DeliveryTube';
import Basin from './lab/shared/Basin';
import InvertedBurette from './lab/shared/InvertedBurette';
import ClampStand from './lab/shared/ClampStand';
import LabBench from './lab/shared/LabBench';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SceneProps {
  currentStep: number;
  isReacting: boolean;
  experimentTime: number;
  buretteReading: number;
  reactionProgress: number;
  isPaused: boolean;
}

// ─── Scene Content ─────────────────────────────────────────────────────────────

const ExperimentSceneContent: React.FC<SceneProps> = ({
  currentStep, isReacting, experimentTime, buretteReading, reactionProgress, isPaused,
}) => {
  const showLiquid = currentStep >= 1;
  const showBurette = currentStep >= 2;
  const showZinc = currentStep >= 3;
  const showStopper = currentStep >= 4;

  return (
    <>
      <ambientLight intensity={0.55} />
      <directionalLight position={[5, 8, 5]} intensity={0.85} color="#ffffff" />
      <directionalLight position={[-3, 4, -2]} intensity={0.3} color="#e8f0f8" />
      <directionalLight position={[0, -2, 3]} intensity={0.15} color="#f0f4f8" />
      <Environment preset="studio" environmentIntensity={0.15} />
      <ContactShadows position={[0, -1.7, 0]} opacity={0.25} scale={8} blur={2.5} far={4} color="#2a3a4a" />
      <LabBench />
      <ConicalFlask showLiquid={showLiquid} showZinc={showZinc} isReacting={isReacting} isPaused={isPaused} experimentTime={experimentTime} reactionProgress={reactionProgress} />
      <RubberStopper visible={showStopper} />
      <DeliveryTube visible={showStopper} isReacting={isReacting} isPaused={isPaused} />
      <Basin visible={showBurette} isReacting={isReacting} isPaused={isPaused} />
      <InvertedBurette visible={showBurette} buretteReading={buretteReading} isReacting={isReacting} isPaused={isPaused} />
      <ClampStand visible={showBurette} />
    </>
  );
};

// ─── Public Component ──────────────────────────────────────────────────────────

const ExperimentScene3D: React.FC<SceneProps> = (props) => {
  return (
    <Canvas dpr={[1, 2]} gl={{ alpha: true, antialias: true }} style={{ background: 'linear-gradient(180deg, #eef4f8 0%, #e0ecf2 100%)' }}>
      <PerspectiveCamera makeDefault position={[0, 1.0, 7]} fov={42} />
      <ExperimentSceneContent {...props} />
      <OrbitControls enableZoom enablePan={false} enableRotate minDistance={4} maxDistance={12} maxPolarAngle={Math.PI / 1.8} minPolarAngle={Math.PI / 6} target={[0, -0.3, 0]} />
    </Canvas>
  );
};

export default ExperimentScene3D;
